import type {
  BusinessAnalysisAoi,
  BusinessAnalysisRadiusKey,
  BusinessAnalysisSalesBenchmark,
  BusinessAnalysisScore,
  BusinessAnalysisSnapshot
} from "../../shared/types";
import type { BusinessAnalysisWeightScenario } from "./businessAnalysisScenario";

type PdfDocument = import("jspdf").jsPDF;
type Rgb = readonly [number, number, number];

export interface BusinessAnalysisPdfInput {
  snapshot: BusinessAnalysisSnapshot;
  aoi: BusinessAnalysisAoi;
  defaultAoi?: BusinessAnalysisAoi;
  detailRadius: BusinessAnalysisRadiusKey;
  weights: BusinessAnalysisWeightScenario;
  customScenario: boolean;
}

export interface BusinessAnalysisPdfOutput {
  filename: string;
  data: ArrayBuffer;
}

const PAGE_WIDTH = 297;
const PAGE_HEIGHT = 210;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const COLORS = {
  teal: [10, 126, 113] as const,
  tealDark: [9, 73, 68] as const,
  tealLight: [222, 245, 240] as const,
  yellow: [235, 177, 48] as const,
  blue: [61, 139, 170] as const,
  ink: [24, 38, 38] as const,
  muted: [91, 108, 106] as const,
  line: [207, 218, 216] as const,
  panel: [246, 249, 248] as const,
  white: [255, 255, 255] as const,
  caution: [174, 92, 53] as const
};

const commercialKeys: Array<keyof BusinessAnalysisWeightScenario> = [
  "fuel", "food", "snacksBeverage", "tobacco", "cigarettes", "cannabis"
];
const demographicKeys: Array<keyof BusinessAnalysisWeightScenario> = [
  "growth", "residentialMarket", "daytimeMarket", "ageProfile"
];
const weightLabels: Record<keyof BusinessAnalysisWeightScenario, string> = {
  fuel: "Fuel",
  food: "Food",
  snacksBeverage: "Snacks & Beverage",
  tobacco: "Vapes / Tobacco",
  cigarettes: "Cigarettes",
  cannabis: "Cannabis",
  growth: "Growth",
  residentialMarket: "Residential market size",
  daytimeMarket: "Daytime market size",
  ageProfile: "Age profile fit"
};
const radarGroups: Array<{ label: string; keys: BusinessAnalysisScore["key"][] }> = [
  { label: "Residential", keys: ["residentialMarket"] },
  { label: "Convenience", keys: ["food", "snacksBeverage"] },
  { label: "Tobacco", keys: ["tobacco"] },
  { label: "Cigarettes", keys: ["cigarettes"] },
  { label: "Cannabis", keys: ["cannabis"] },
  { label: "Fuel", keys: ["fuel"] }
];

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function cleanText(value: string | undefined): string {
  return (value || "").replace(/[\u2010-\u2015]/g, "-").replace(/[^\x20-\x7E\xA0-\xFF]/g, "");
}

function safeFilename(value: string): string {
  return cleanText(value).replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "AOI";
}

function formatScore(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatMoney(value: number): string {
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (absolute >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (absolute >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatMetric(score: BusinessAnalysisScore, benchmark = false): string {
  const value = benchmark ? score.benchmarkValue : score.value;
  if (score.unit === "currency") return formatMoney(value);
  if (score.unit === "percent") return `${(value * 100).toFixed(1)}%`;
  return Math.round(value).toLocaleString("en-CA");
}

function humanDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? cleanText(value)
    : new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "long", day: "numeric" }).format(date);
}

function scoreColor(score: number): readonly [number, number, number] {
  if (score >= 80) return COLORS.teal;
  if (score >= 65) return COLORS.blue;
  if (score >= 50) return COLORS.yellow;
  return COLORS.caution;
}

function setFill(doc: PdfDocument, color: readonly [number, number, number]): void {
  doc.setFillColor(color[0], color[1], color[2]);
}

function setDraw(doc: PdfDocument, color: readonly [number, number, number]): void {
  doc.setDrawColor(color[0], color[1], color[2]);
}

function setText(doc: PdfDocument, color: readonly [number, number, number]): void {
  doc.setTextColor(color[0], color[1], color[2]);
}

function text(doc: PdfDocument, value: string, x: number, y: number, options?: Parameters<PdfDocument["text"]>[3]): void {
  doc.text(cleanText(value), x, y, options);
}

function wrappedText(
  doc: PdfDocument,
  value: string,
  x: number,
  y: number,
  width: number,
  lineHeight = 4.4,
  maxLines = 5
): number {
  const lines = doc.splitTextToSize(cleanText(value), width).slice(0, maxLines) as string[];
  lines.forEach((line, index) => doc.text(line, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

function pageFrame(doc: PdfDocument, input: BusinessAnalysisPdfInput, page: number, title: string): void {
  setFill(doc, COLORS.tealDark);
  doc.rect(0, 0, PAGE_WIDTH, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setText(doc, COLORS.tealLight);
  text(doc, "GEN7 FUEL - BUSINESS ANALYSIS", MARGIN, 7.5);
  doc.setFont("helvetica", "normal");
  text(doc, `${input.aoi.name} | AOI ${input.aoi.aoiSk}`, MARGIN, 13);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  setText(doc, COLORS.ink);
  text(doc, title, MARGIN, 31);
  setDraw(doc, COLORS.line);
  doc.setLineWidth(0.35);
  doc.line(MARGIN, 36, PAGE_WIDTH - MARGIN, 36);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.muted);
  text(doc, `Confidential - Gen7 Fuel | Model ${input.snapshot.methodologyVersion} | ${humanDate(input.snapshot.asOfDate)}`, MARGIN, 203);
  text(doc, `Page ${page} of 6`, PAGE_WIDTH - MARGIN, 203, { align: "right" });
}

function panel(doc: PdfDocument, x: number, y: number, width: number, height: number, fill: Rgb = COLORS.panel): void {
  setFill(doc, fill);
  setDraw(doc, COLORS.line);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, height, 2, 2, "FD");
}

function pill(doc: PdfDocument, value: string, x: number, y: number, width: number, color: Rgb = COLORS.teal, foreground: Rgb = COLORS.white): void {
  setFill(doc, color);
  doc.roundedRect(x, y, width, 7, 3.5, 3.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  setText(doc, foreground);
  text(doc, value, x + width / 2, y + 4.7, { align: "center" });
}

function metricCard(doc: PdfDocument, x: number, y: number, width: number, label: string, value: string, note: string): void {
  panel(doc, x, y, width, 30, COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  setText(doc, COLORS.muted);
  text(doc, label.toUpperCase(), x + 4, y + 7);
  doc.setFontSize(value.length > 18 ? 11.5 : 18);
  setText(doc, COLORS.ink);
  const valueLines = doc.splitTextToSize(cleanText(value), width - 8).slice(0, 2) as string[];
  valueLines.forEach((line, index) => doc.text(line, x + 4, y + 16 + index * 5));
  doc.setFont("helvetica", "normal");
  doc.setFontSize(valueLines.length > 1 ? 6.5 : 7.5);
  setText(doc, COLORS.muted);
  wrappedText(doc, note, x + 4, valueLines.length > 1 ? y + 27 : y + 24, width - 8, 3.2, valueLines.length > 1 ? 1 : 2);
}

function sectionLabel(doc: PdfDocument, value: string, x: number, y: number): void {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setText(doc, COLORS.teal);
  text(doc, value.toUpperCase(), x, y);
}

function resultFor(input: BusinessAnalysisPdfInput, radius: BusinessAnalysisRadiusKey) {
  return input.aoi.results.find((item) => item.radiusKey === radius) || input.aoi.results[0];
}

function detailResult(input: BusinessAnalysisPdfInput) {
  return resultFor(input, input.detailRadius);
}

function comparableSales(input: BusinessAnalysisPdfInput): BusinessAnalysisSalesBenchmark[] {
  const result = detailResult(input);
  const key = result.comparableStation.poiSk?.trim().toLowerCase();
  const name = result.comparableStation.name.trim().toLowerCase();
  return input.snapshot.salesBenchmarks.filter((row) =>
    Boolean(key && row.stationSk.trim().toLowerCase() === key) || row.stationName.trim().toLowerCase() === name
  );
}

function relativePhrase(label: string, index: number): string {
  const difference = Math.abs(index - 100);
  if (difference < 1) return `${label} is broadly in line with the comparable station`;
  return `${label} is ${difference}% ${index > 100 ? "above" : "below"} the comparable station`;
}

function profileNarrative(scores: BusinessAnalysisScore[], stationName: string): string {
  const values = radarValues(scores).map((item) => ({ ...item, gap: Math.round(item.aoi - item.benchmark) }));
  const strongest = [...values].sort((left, right) => right.gap - left.gap)[0];
  const weakest = [...values].sort((left, right) => left.gap - right.gap)[0];
  if (!strongest || !weakest) return `The AOI profile is compared with ${stationName} using operating-station percentiles.`;
  const weakestPhrase = weakest.gap >= 0
    ? `${weakest.label} is the narrowest advantage at +${weakest.gap} points`
    : `${weakest.label} trails by ${Math.abs(weakest.gap)} points`;
  return `The strongest profile advantage versus ${stationName} is ${strongest.label} (+${strongest.gap} percentile points), while ${weakestPhrase}. This identifies the characteristics driving the opportunity score and where field evidence should be tested.`;
}

function commercialNarrative(result: ReturnType<typeof detailResult>): string {
  const strongest = [...result.categories].sort((left, right) => right.index - left.index)[0];
  const weakest = [...result.categories].sort((left, right) => left.index - right.index)[0];
  if (!strongest || !weakest) return "Category expenditure potential is compared with the selected operating-station benchmark.";
  return `${relativePhrase(strongest.category, strongest.index)}, the clearest category opportunity in the ${result.radiusMinutes}-minute trade area. ${relativePhrase(weakest.category, weakest.index)}. The bars show absolute annual potential; the index isolates relative market strength.`;
}

function realizationNarrative(rows: BusinessAnalysisSalesBenchmark[], stationName: string): string {
  if (!rows.length) return `No matching CSO sales data was returned for ${stationName}; the Esri opportunity evidence remains available.`;
  const strongest = [...rows].sort((left, right) => right.realizationRate - left.realizationRate)[0];
  const weakest = [...rows].sort((left, right) => left.realizationRate - right.realizationRate)[0];
  return `${strongest.category} has the highest observed realization at ${strongest.realizationRate.toFixed(1)}%, while ${weakest.category} is lowest at ${weakest.realizationRate.toFixed(1)}%. This calibrates how market potential has converted to sales at the comparable station, but it is not a direct AOI forecast.`;
}

function drawPageOne(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 1, `${input.aoi.name} Development Assessment`);
  const result = detailResult(input);
  const location = [input.aoi.address, input.aoi.city, input.aoi.province].filter(Boolean).join(", ");
  pill(
    doc,
    input.customScenario ? "CUSTOM SENSITIVITY SCENARIO" : "BA-2.0 DEFAULT",
    220,
    23,
    63,
    input.customScenario ? COLORS.yellow : COLORS.teal,
    input.customScenario ? COLORS.tealDark : COLORS.white
  );

  sectionLabel(doc, "Executive Summary", MARGIN, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setText(doc, COLORS.ink);
  text(doc, `${input.aoi.recommendation}: ${formatScore(input.aoi.overallScore)} / 100`, MARGIN, 54);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.muted);
  wrappedText(doc, location || `${input.aoi.city || "Location not provided"}, ${input.aoi.province || ""}`, MARGIN, 60, 168, 4.2, 2);

  setDraw(doc, scoreColor(input.aoi.overallScore));
  doc.setLineWidth(4);
  doc.circle(250, 61, 19, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(23);
  setText(doc, COLORS.ink);
  text(doc, formatScore(input.aoi.overallScore), 250, 64, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  setText(doc, COLORS.muted);
  text(doc, "OVERALL SCORE", 250, 72, { align: "center" });

  const bullets = [
    result.highlights[0] || `${result.radiusMinutes}-minute opportunity score is ${formatScore(result.opportunityScore)} against operating-station benchmarks.`,
    result.highlights[1] || `${result.comparableStation.name} is the closest operating-station profile at ${formatScore(result.stationFitScore)}% similarity.`,
    result.risks[0] || "No material Esri demographic risk surfaced; field validation is still required."
  ];
  let bulletY = 78;
  bullets.forEach((item, index) => {
    setFill(doc, index === 2 ? COLORS.yellow : COLORS.teal);
    doc.circle(MARGIN + 1.5, bulletY - 1.2, 1.3, "F");
    doc.setFont("helvetica", index === 0 ? "bold" : "normal");
    doc.setFontSize(8.5);
    setText(doc, COLORS.ink);
    bulletY = wrappedText(doc, item, MARGIN + 6, bulletY, 176, 4.4, 2) + 2;
  });

  const totalPotential = sum(result.categories.map((item) => item.aoiExpenditure));
  metricCard(doc, MARGIN, 109, 83, `${result.radiusMinutes}-minute suitability`, formatScore(result.suitabilityScore), `${result.grade}; opportunity ${formatScore(result.opportunityScore)}`);
  metricCard(doc, MARGIN + 90, 109, 83, "Comparable station", result.comparableStation.name, `${formatScore(result.stationFitScore)}% normalized profile similarity`);
  metricCard(doc, MARGIN + 180, 109, 89, `${result.radiusMinutes}-minute market potential`, formatMoney(totalPotential), "Esri annual category expenditure");

  sectionLabel(doc, "Trade Area Score Progression", MARGIN, 150);
  (["R05", "R10", "R20"] as BusinessAnalysisRadiusKey[]).forEach((radius, index) => {
    const radiusResult = resultFor(input, radius);
    const x = MARGIN + index * 91;
    panel(doc, x, 156, 83, 30, index === 2 ? COLORS.tealLight : COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    setText(doc, scoreColor(radiusResult.suitabilityScore));
    text(doc, formatScore(radiusResult.suitabilityScore), x + 5, 169);
    doc.setFontSize(8);
    setText(doc, COLORS.ink);
    text(doc, `${radiusResult.radiusMinutes}-minute trade area`, x + 28, 165);
    doc.setFont("helvetica", "normal");
    setText(doc, COLORS.muted);
    text(doc, `${radiusResult.grade} | opportunity ${formatScore(radiusResult.opportunityScore)}`, x + 28, 172);
  });

  if (input.customScenario && input.defaultAoi) {
    const delta = input.aoi.overallScore - input.defaultAoi.overallScore;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    setText(doc, COLORS.caution);
    text(doc, `Sensitivity impact: BA-2.0 default ${formatScore(input.defaultAoi.overallScore)} | current ${formatScore(input.aoi.overallScore)} | ${delta >= 0 ? "+" : ""}${formatScore(delta)} points`, MARGIN, 193);
  }
}

function weightBar(doc: PdfDocument, x: number, y: number, width: number, label: string, value: number, max: number, color: readonly [number, number, number]): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.ink);
  text(doc, label, x, y);
  setText(doc, COLORS.muted);
  text(doc, `${formatScore(value)}%`, x + width, y, { align: "right" });
  setFill(doc, COLORS.line);
  doc.roundedRect(x, y + 2, width, 3.2, 1.6, 1.6, "F");
  setFill(doc, color);
  doc.roundedRect(x, y + 2, Math.max(0.8, width * (value / Math.max(1, max))), 3.2, 1.6, 1.6, "F");
}

function drawPageTwo(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 2, "How the Score Is Calculated");
  const commercialTotal = sum(commercialKeys.map((key) => input.weights[key]));
  const demographicTotal = sum(demographicKeys.map((key) => input.weights[key]));
  sectionLabel(doc, "Decision Model", MARGIN, 45);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, `This scenario assigns ${formatScore(commercialTotal)}% of opportunity to commercial potential and ${formatScore(demographicTotal)}% to demographic context. Opportunity is combined with comparable-station fit, then weighted toward the broader 20-minute trade area to create the final screening score.`, MARGIN, 52, CONTENT_WIDTH, 4.4, 3);

  const formulas = [
    ["1. Opportunity by radius", "Sum(metric percentile x applied weight) / 100"],
    ["2. Suitability by radius", "Opportunity x 75% + comparable station fit x 25%"],
    ["3. Overall AOI score", "5-minute x 25% + 10-minute x 35% + 20-minute x 40%"]
  ];
  formulas.forEach(([label, formula], index) => {
    const x = MARGIN + index * 91;
    panel(doc, x, 67, 83, 29, COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    setText(doc, COLORS.teal);
    text(doc, label, x + 4, 75);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setText(doc, COLORS.ink);
    wrappedText(doc, formula, x + 4, 83, 75, 4, 3);
  });

  panel(doc, MARGIN, 104, 130, 76, COLORS.white);
  panel(doc, MARGIN + 139, 104, 130, 76, COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  setText(doc, COLORS.ink);
  text(doc, `Commercial potential - ${formatScore(commercialTotal)}%`, MARGIN + 5, 114);
  text(doc, `Demographic context - ${formatScore(demographicTotal)}%`, MARGIN + 144, 114);

  commercialKeys.forEach((key, index) => weightBar(doc, MARGIN + 5, 124 + index * 8.5, 120, weightLabels[key], input.weights[key], Math.max(1, commercialTotal), COLORS.teal));
  demographicKeys.forEach((key, index) => weightBar(doc, MARGIN + 144, 124 + index * 12, 120, weightLabels[key], input.weights[key], Math.max(1, demographicTotal), COLORS.yellow));

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.muted);
  text(doc, "Score interpretation: 80+ Prioritize | 65-79 Advance with validation | 50-64 Monitor | below 50 Do not prioritize", MARGIN, 190);
}

function radarValues(scores: BusinessAnalysisScore[]) {
  return radarGroups.map((group) => {
    const matching = scores.filter((score) => group.keys.includes(score.key));
    const divisor = matching.length || 1;
    return {
      label: group.label,
      aoi: sum(matching.map((score) => score.score)) / divisor,
      benchmark: sum(matching.map((score) => score.benchmarkScore)) / divisor
    };
  });
}

function polygonPoints(cx: number, cy: number, radius: number, count: number, values?: number[]): Array<[number, number]> {
  return Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + index * (Math.PI * 2 / count);
    const scale = values ? Math.max(0, Math.min(100, values[index])) / 100 : 1;
    return [cx + Math.cos(angle) * radius * scale, cy + Math.sin(angle) * radius * scale];
  });
}

function drawPolygon(doc: PdfDocument, points: Array<[number, number]>, style: "S" | "F" | "FD"): void {
  const [first, ...rest] = points;
  const lines = rest.map(([x, y], index) => [x - (index === 0 ? first[0] : rest[index - 1][0]), y - (index === 0 ? first[1] : rest[index - 1][1])]);
  lines.push([first[0] - rest[rest.length - 1][0], first[1] - rest[rest.length - 1][1]]);
  doc.lines(lines, first[0], first[1], [1, 1], style, true);
}

function drawRadar(doc: PdfDocument, x: number, y: number, values: ReturnType<typeof radarValues>): void {
  const cx = x + 54;
  const cy = y + 55;
  const radius = 38;
  setDraw(doc, COLORS.line);
  doc.setLineWidth(0.3);
  [0.25, 0.5, 0.75, 1].forEach((scale) => drawPolygon(doc, polygonPoints(cx, cy, radius * scale, values.length), "S"));
  polygonPoints(cx, cy, radius, values.length).forEach(([px, py]) => doc.line(cx, cy, px, py));

  setFill(doc, COLORS.tealLight);
  setDraw(doc, COLORS.teal);
  doc.setLineWidth(1.2);
  drawPolygon(doc, polygonPoints(cx, cy, radius, values.length, values.map((item) => item.aoi)), "FD");
  setDraw(doc, COLORS.blue);
  doc.setLineDashPattern([1.5, 1.2], 0);
  doc.setLineWidth(1.2);
  drawPolygon(doc, polygonPoints(cx, cy, radius, values.length, values.map((item) => item.benchmark)), "S");
  doc.setLineDashPattern([], 0);

  polygonPoints(cx, cy, radius + 8, values.length).forEach(([px, py], index) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    setText(doc, COLORS.ink);
    text(doc, values[index].label, px, py, { align: px < cx - 3 ? "right" : px > cx + 3 ? "left" : "center" });
  });
  setFill(doc, COLORS.teal);
  doc.rect(x + 22, y + 104, 4, 2, "F");
  setText(doc, COLORS.muted);
  text(doc, "AOI", x + 28, y + 106);
  setDraw(doc, COLORS.blue);
  doc.setLineDashPattern([1.5, 1.2], 0);
  doc.line(x + 54, y + 105, x + 60, y + 105);
  doc.setLineDashPattern([], 0);
  text(doc, "Comparable", x + 63, y + 106);
}

function drawPageThree(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 3, "AOI Opportunity Profile");
  const result = detailResult(input);
  const values = radarValues(result.scores);
  sectionLabel(doc, "Normalized Benchmark Comparison", MARGIN, 45);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, profileNarrative(result.scores, result.comparableStation.name), MARGIN, 52, CONTENT_WIDTH, 4.4, 3);
  panel(doc, MARGIN, 67, 116, 118, COLORS.white);
  drawRadar(doc, MARGIN + 4, 69, values);

  const tableX = 142;
  const tableY = 70;
  const rowHeight = 15;
  setFill(doc, COLORS.tealDark);
  doc.rect(tableX, tableY, 141, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  setText(doc, COLORS.white);
  text(doc, "PROFILE METRIC", tableX + 4, tableY + 6.5);
  text(doc, "AOI", tableX + 92, tableY + 6.5, { align: "right" });
  text(doc, "COMPARABLE", tableX + 118, tableY + 6.5, { align: "right" });
  text(doc, "GAP", tableX + 136, tableY + 6.5, { align: "right" });
  values.forEach((item, index) => {
    const y = tableY + 10 + index * rowHeight;
    setFill(doc, index % 2 ? COLORS.panel : COLORS.white);
    doc.rect(tableX, y, 141, rowHeight, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setText(doc, COLORS.ink);
    text(doc, item.label, tableX + 4, y + 9);
    text(doc, formatScore(Math.round(item.aoi)), tableX + 92, y + 9, { align: "right" });
    text(doc, formatScore(Math.round(item.benchmark)), tableX + 118, y + 9, { align: "right" });
    const gap = Math.round(item.aoi - item.benchmark);
    setText(doc, gap >= 0 ? COLORS.teal : COLORS.caution);
    text(doc, `${gap >= 0 ? "+" : ""}${gap}`, tableX + 136, y + 9, { align: "right" });
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.muted);
  wrappedText(doc, "So what: the profile highlights where the AOI is structurally stronger or weaker than the most comparable operating station. It does not measure traffic, competition or parcel economics.", tableX, 174, 141, 4, 3);
}

function drawPageFour(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 4, "Commercial Market Potential");
  const result = detailResult(input);
  sectionLabel(doc, "Esri Annual Category Expenditure", MARGIN, 45);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, commercialNarrative(result), MARGIN, 52, CONTENT_WIDTH, 4.4, 3);

  const maxValue = Math.max(1, ...result.categories.flatMap((item) => [item.aoiExpenditure, item.benchmarkExpenditure]));
  const labelX = MARGIN;
  const barX = 58;
  const barWidth = 150;
  const valueX = 240;
  const indexX = 281;
  result.categories.forEach((item, index) => {
    const y = 70 + index * 18.5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    setText(doc, COLORS.ink);
    text(doc, item.category, labelX, y + 6);
    setFill(doc, COLORS.line);
    doc.roundedRect(barX, y, barWidth, 5.2, 1.2, 1.2, "F");
    setFill(doc, COLORS.teal);
    doc.roundedRect(barX, y, barWidth * item.aoiExpenditure / maxValue, 5.2, 1.2, 1.2, "F");
    setFill(doc, COLORS.line);
    doc.roundedRect(barX, y + 7, barWidth, 4, 1, 1, "F");
    setFill(doc, COLORS.blue);
    doc.roundedRect(barX, y + 7, barWidth * item.benchmarkExpenditure / maxValue, 4, 1, 1, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    setText(doc, COLORS.ink);
    text(doc, `${formatMoney(item.aoiExpenditure)} AOI`, valueX, y + 4, { align: "right" });
    setText(doc, COLORS.muted);
    text(doc, `${formatMoney(item.benchmarkExpenditure)} comparable`, valueX, y + 10.2, { align: "right" });
    doc.setFont("helvetica", "bold");
    setText(doc, item.index >= 100 ? COLORS.teal : COLORS.caution);
    text(doc, `${item.index}%`, indexX, y + 7, { align: "right" });
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.muted);
  text(doc, "Index 100 = equal to comparable station | above 100 = greater AOI expenditure potential", MARGIN, 190);
}

function drawPageFive(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 5, "Comparable Station Realization");
  const result = detailResult(input);
  const rows = comparableSales(input);
  sectionLabel(doc, "CSO Actual Sales / Esri Annual Expenditure", MARGIN, 45);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, realizationNarrative(rows, result.comparableStation.name), MARGIN, 52, CONTENT_WIDTH, 4.4, 3);

  const tableY = 70;
  setFill(doc, COLORS.tealDark);
  doc.rect(MARGIN, tableY, CONTENT_WIDTH, 11, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  setText(doc, COLORS.white);
  text(doc, "CATEGORY", MARGIN + 4, tableY + 7);
  text(doc, "CSO ACTUAL SALES", 142, tableY + 7, { align: "right" });
  text(doc, "ESRI EXPENDITURE", 216, tableY + 7, { align: "right" });
  text(doc, "REALIZATION", 279, tableY + 7, { align: "right" });

  if (rows.length === 0) {
    panel(doc, MARGIN, 88, CONTENT_WIDTH, 42, COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setText(doc, COLORS.caution);
    text(doc, "Actual sales benchmark unavailable", MARGIN + 6, 101);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    setText(doc, COLORS.muted);
    wrappedText(doc, `No matching CSO sales data was returned for ${result.comparableStation.name}. Esri market-potential evidence remains available elsewhere in this report.`, MARGIN + 6, 111, CONTENT_WIDTH - 12, 4.4, 3);
  } else {
    rows.forEach((row, index) => {
      const y = tableY + 11 + index * 15;
      setFill(doc, index % 2 ? COLORS.panel : COLORS.white);
      doc.rect(MARGIN, y, CONTENT_WIDTH, 15, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      setText(doc, COLORS.ink);
      text(doc, row.category, MARGIN + 4, y + 9.5);
      text(doc, formatMoney(row.actualSales), 142, y + 9.5, { align: "right" });
      text(doc, formatMoney(row.esriExpenditure), 216, y + 9.5, { align: "right" });
      doc.setFont("helvetica", "bold");
      setText(doc, COLORS.teal);
      text(doc, `${row.realizationRate.toFixed(1)}%`, 279, y + 9.5, { align: "right" });
    });
  }

  panel(doc, MARGIN, 169, CONTENT_WIDTH, 22, COLORS.tealLight);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  setText(doc, COLORS.tealDark);
  text(doc, "INTERPRETATION NOTE", MARGIN + 4, 176);
  doc.setFont("helvetica", "normal");
  setText(doc, COLORS.ink);
  wrappedText(doc, "Actual sales periods differ by station and may be year-to-date, while Esri expenditure represents annual potential. Compare realization directionally unless the sales period has been annualized and aligned.", MARGIN + 4, 182, CONTENT_WIDTH - 8, 3.8, 2);
}

function bulletList(doc: PdfDocument, items: string[], x: number, y: number, width: number, color: Rgb = COLORS.teal): number {
  let nextY = y;
  items.forEach((item) => {
    setFill(doc, color);
    doc.circle(x + 1.2, nextY - 1.1, 1, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setText(doc, COLORS.ink);
    nextY = wrappedText(doc, item, x + 5, nextY, width - 5, 4, 3) + 2;
  });
  return nextY;
}

function drawPageSix(doc: PdfDocument, input: BusinessAnalysisPdfInput): void {
  pageFrame(doc, input, 6, "Recommendation and Next Validation");
  const result = detailResult(input);
  const nextSteps = input.aoi.recommendation === "Prioritize" || input.aoi.recommendation === "Advance with validation"
    ? [
        "Confirm Kalibrate traffic volume, direction and daypart patterns.",
        "Map nearby competitors and assess access, visibility and turning movements.",
        "Validate parcel cost, construction feasibility, permitting and environmental constraints.",
        "Build project economics using fuel volume, inside sales, margin and payback assumptions."
      ]
    : [
        "Retain the AOI in the pipeline and identify the weakest score drivers.",
        "Collect traffic and competitive evidence before changing its priority.",
        "Re-run the approved BA-2.0 scenario when new Esri or site evidence becomes available."
      ];

  panel(doc, MARGIN, 44, CONTENT_WIDTH, 28, COLORS.tealLight);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setText(doc, COLORS.tealDark);
  text(doc, `${input.aoi.recommendation} - ${formatScore(input.aoi.overallScore)} / 100`, MARGIN + 6, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, `${result.radiusMinutes}-minute evidence supports an opportunity score of ${formatScore(result.opportunityScore)} and ${formatScore(result.stationFitScore)}% similarity to ${result.comparableStation.name}. This is a screening recommendation, not a final investment approval.`, MARGIN + 6, 63, CONTENT_WIDTH - 12, 4, 2);

  const colWidth = 128;
  sectionLabel(doc, "Recommended Next Steps", MARGIN, 84);
  bulletList(doc, nextSteps, MARGIN, 93, colWidth);
  sectionLabel(doc, "Further Questions", 155, 84);
  bulletList(doc, [
    "Does verified traffic support the demand implied by the Esri trade area?",
    "Can the parcel capture traffic safely and visibly in both travel directions?",
    "How does competitor density change the achievable realization rate?",
    "Does the project meet Gen7 return and payback requirements?"
  ], 155, 93, 128, COLORS.yellow);

  sectionLabel(doc, "Caveats and Assumptions", MARGIN, 145);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setText(doc, COLORS.ink);
  wrappedText(doc, "Kalibrate traffic, competition, parcel access, environmental review and project economics are not yet included in the score. Percentile ranks depend on the current Gen7 operating-station benchmark set. Custom weights are sensitivity analysis and do not replace the approved BA-2.0 baseline.", MARGIN, 153, 132, 3.8, 5);

  sectionLabel(doc, "Data Foundation", 155, 145);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  setText(doc, COLORS.ink);
  wrappedText(doc, "Esri.Dim_Metrics: metric definitions and demographic context. Esri.Dim_POI: AOI and station identity. Esri.Dim_Radius: 5, 10 and 20-minute trade areas. Esri.Fact_Metrics: market and expenditure values. CSO.Sales, CSO.ItemBookCSO and CSO.FuelSummary: comparable-station realized sales.", 155, 153, 128, 3.8, 6);
}

export async function createBusinessAnalysisPdf(input: BusinessAnalysisPdfInput): Promise<BusinessAnalysisPdfOutput> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  doc.setProperties({
    title: `${input.aoi.name} Business Analysis`,
    subject: `AOI ${input.aoi.aoiSk} development suitability assessment`,
    author: "Gen7 Fuel",
    creator: `Business Analysis ${input.snapshot.methodologyVersion}`
  });
  drawPageOne(doc, input);
  [drawPageTwo, drawPageThree, drawPageFour, drawPageFive, drawPageSix].forEach((drawPage) => {
    doc.addPage();
    drawPage(doc, input);
  });
  const datePart = input.snapshot.asOfDate.slice(0, 10) || new Date().toISOString().slice(0, 10);
  const scenarioPart = input.customScenario ? "Custom" : "BA-2.0";
  return {
    filename: `Business_Analysis_${safeFilename(input.aoi.name)}_${safeFilename(input.aoi.aoiSk)}_${scenarioPart}_${datePart}.pdf`,
    data: doc.output("arraybuffer")
  };
}

export async function downloadBusinessAnalysisPdf(input: BusinessAnalysisPdfInput): Promise<string> {
  const output = await createBusinessAnalysisPdf(input);
  const blob = new Blob([output.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = output.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return output.filename;
}
